import React, { useState, useRef } from 'react';
import './DocumentUploadModal.css';

const typeMap = {
    'RC': 'Registration Certificate',
    'DL': 'Driver License',
    'PUC': 'PUC Certificate',
    'INSURANCE': 'Insurance',
    'FITNESS': 'Fitness Permit',
    'PERMIT': 'Permit',
    'OTHER': 'Other'
};

function DocumentUploadModal({ isOpen, onClose, vehicleId, onUploadSuccess }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [documentType, setDocumentType] = useState('RC');
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // No file type restrictions - accept all formats
            setSelectedFile(file);
            setError('');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files[0];
        if (file) {
            setSelectedFile(file);
            setError('');
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file to upload');
            return;
        }

        setIsUploading(true);
        setError('');
        setUploadProgress(0);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Authentication required. Please login again.');
            }

            const formData = new FormData();
            formData.append('documents', selectedFile);
            formData.append('document_type', documentType);

            // Simulate progress for better UX
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return prev;
                    }
                    return prev + 10;
                });
            }, 200);

            const response = await fetch(
                `https://api.rtoxpert.com/api/vehicles/${vehicleId}/documents`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                }
            );

            clearInterval(progressInterval);

            if (!response.ok) {
                const result = await response.json();
                throw new Error(result.message || 'Failed to upload document');
            }

            setUploadProgress(100);

            // Reset form and close modal
            setTimeout(() => {
                setSelectedFile(null);
                setDocumentType('RC');
                setUploadProgress(0);
                onUploadSuccess?.();
                onClose();
            }, 500);

        } catch (err) {
            setError(err.message || 'Upload failed. Please try again.');
            setUploadProgress(0);
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        if (!isUploading) {
            setSelectedFile(null);
            setDocumentType('RC');
            setError('');
            setUploadProgress(0);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="document-upload-overlay" onClick={handleClose}>
            <div className="document-upload-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Upload Document</h2>
                    <button
                        className="close-btn"
                        onClick={handleClose}
                        disabled={isUploading}
                        aria-label="Close modal"
                    >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="modal-body">
                    {/* Document Type Selector */}
                    <div className="form-group">
                        <label htmlFor="documentType">Document Type</label>
                        <select
                            id="documentType"
                            value={documentType}
                            onChange={(e) => setDocumentType(e.target.value)}
                            disabled={isUploading}
                        >
                            {Object.entries(typeMap).map(([key, value]) => (
                                <option key={key} value={key}>{value}</option>
                            ))}
                        </select>
                    </div>

                    {/* File Drop Zone */}
                    <div
                        className={`file-drop-zone ${selectedFile ? 'has-file' : ''} ${isUploading ? 'uploading' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={() => !isUploading && fileInputRef.current?.click()}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                            disabled={isUploading}
                        />

                        {selectedFile ? (
                            <div className="selected-file">
                                <div className="file-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div className="file-info">
                                    <span className="file-name">{selectedFile.name}</span>
                                    <span className="file-size">{formatFileSize(selectedFile.size)}</span>
                                </div>
                                {!isUploading && (
                                    <button
                                        className="remove-file-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFile(null);
                                        }}
                                        aria-label="Remove file"
                                    >
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="drop-zone-content">
                                <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="drop-text">Drag & drop file here or <span>browse</span></p>
                                <p className="supported-text">All file types supported (PDF, Images, etc.)</p>
                            </div>
                        )}
                    </div>

                    {/* Upload Progress */}
                    {isUploading && (
                        <div className="upload-progress">
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                            <span className="progress-text">{uploadProgress}% Uploading...</span>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="error-message">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {error}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button
                        className="cancel-btn"
                        onClick={handleClose}
                        disabled={isUploading}
                    >
                        Cancel
                    </button>
                    <button
                        className="upload-btn"
                        onClick={handleUpload}
                        disabled={!selectedFile || isUploading}
                    >
                        {isUploading ? 'Uploading...' : 'Upload Document'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DocumentUploadModal;
